import { getAccessToken } from "@/shared/api/client";

/**
 * 将外部海报/封面 URL 转为后端图片代理地址。
 *
 * 豆瓣等源对浏览器直链常返回 418。代理走 `/api/v1/system/cache/image`，
 * 并附带 `access_token`（登录 JWT），因 `<img>` 无法带 Authorization，
 * 且 resource Cookie 在 localhost/127.0.0.1 不一致时容易丢失。
 */
export function proxyImageUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  const raw = url.trim();
  if (!raw) return undefined;

  // data / blob 不代理
  if (raw.startsWith("data:") || raw.startsWith("blob:")) {
    return raw;
  }

  // 已是后端代理：补全 access_token（若缺失）
  if (
    raw.startsWith("/api/v1/system/img") ||
    raw.startsWith("/api/v1/system/cache/image")
  ) {
    return withAccessToken(raw);
  }

  // 同源相对路径（非外链）原样返回
  if (raw.startsWith("/") && !raw.startsWith("//")) {
    return raw;
  }

  let absolute = raw;
  try {
    const u = new URL(raw, typeof window !== "undefined" ? window.location.origin : "http://local");
    if (typeof window !== "undefined" && u.origin === window.location.origin) {
      return u.pathname + u.search;
    }
    absolute = u.href;
  } catch {
    return undefined;
  }

  const params = new URLSearchParams();
  params.set("url", absolute);
  const token = getAccessToken();
  if (token) {
    params.set("access_token", token);
  }
  return `/api/v1/system/cache/image?${params.toString()}`;
}

function withAccessToken(pathAndQuery: string): string {
  const token = getAccessToken();
  if (!token) return pathAndQuery;
  if (pathAndQuery.includes("access_token=")) return pathAndQuery;
  const sep = pathAndQuery.includes("?") ? "&" : "?";
  return `${pathAndQuery}${sep}access_token=${encodeURIComponent(token)}`;
}
